import { FunctionComponent, h, render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
// @ts-ignore
import { TinyDatePicker } from 'tiny-date-picker';

enum ControlGroupCategory {
  eeo = "eeo",
  ofccp = "ofccp",
  other = "",
  vevraa = "vevraa"
}

enum QuestionType {
  // suggested or custom
  text = "text",
  textarea = "textarea",
  file = "file",
  date = "date",
  // backend renders boolean as a select with options "no answer", "yes", "no"
  // suggested
  email = "email",
  // backend renders location as text inputs
  // backend renders eeo as selects
  // backend renders vevraa as select
  // backend renders ofccp as select
}

enum OptionEnabledQuestionType {
  radio = "radio",
  select = "select",
  checkbox = "checkbox",
}

enum NoResponseOptionQuestions {
  eeo = "eeo",
  ofccp = "ofccp",
  vevraa = "vevraa",
}

interface IQuestion {
  id: string,
  label: string,
  required: boolean,
  options?: string[],
  type: QuestionType | OptionEnabledQuestionType,
  validation: {
    max?: number,
    min?: number
  }
}

export interface IApplyProps {
  context: {
    role_id: string,
    preview: boolean,
    questions: IQuestion[],
    question_groups: string[][],
  }
}

interface IApplyControlGroupProps {
  category: ControlGroupCategory,
  errors: IValidationError[],
  onBeforeSubmit: Number,
  questions: IQuestion[],
}

interface IApplyControlProps extends IQuestion {
  errors: IValidationError[],
  groupCategory: ControlGroupCategory,
  onBeforeSubmit: Number,
}

interface IFileToUpload {
  id: string,
  file: File
}

interface IValidationError {
  id: string,
  message: string,
}

interface IValidationSuccessBody {
  submit_jwt: string,
}

interface IValidationErrorBody {
  message?: string,
  context?: string[],
  errors?: IValidationError[],
}

interface IValidationResponseBody {
  body: IValidationErrorBody | IValidationSuccessBody,
  status: number
}

const generateIdAttribute = (id: string, suffix: string = ""): string => {
  return `${id}${suffix}`;
}

const generateRequiredIndicator = (required: boolean) => {
  if (required) {
    return <span class="required">Required</span>
  } else {
    return "";
  }
}

// Represents a group of questions
// Most questions are a group of 1, but some like eeo and address are
// comprised of several questions. Some may have some text to display before
// and/or after all of the questions in the group.
const ApplyControlGroup: FunctionComponent<IApplyControlGroupProps> = ({ category, onBeforeSubmit, questions, errors }) => {
  const afterBlock = () => {
    let after;
    if (category === ControlGroupCategory.ofccp) {
      after = (<div class="form-control">
        <p>PUBLIC BURDEN STATEMENT: According to the Paperwork Reduction Act of 1995 no persons are required to respond to a collection of information unless such collection displays a valid OMB control number. This survey should take about 5 minutes to complete.</p>
      </div>);
    }
    return after;
  };

  const beforeBlock = () => {
    let before;
    if (category === ControlGroupCategory.eeo) {
      before = (<div class="form-control">
        <h2>U.S. Equal Employment Opportunity information</h2>
        <p>Completion is voluntary and will not subject you to adverse treatment.</p>
        <p>We provide equal employment and affirmative action opportunities to applicants and employees without regard to race, color, religion, sex, sexual orientation, gender identity, national origin, protected veteran status, or disability.</p>
        <p>We are a federal contractor or subcontractor subject to certain governmental recordkeeping and reporting requirements for the administration of civil right laws and regulations. Employment decisions are made on the basis of job-related criteria without regard to race, ethnicity, color, religion, sex, sexual orientation, gender identity, marital status, age, genetic information, national origin, disability, military, or veteran status, or any other classification protected by applicable law.</p>
        <p>We invite all applicants to voluntarily self-identify their race, ethnicity, and gender. Submission of the information on this form is strictly voluntary and refusal to provide it will not subject you to any adverse treatment. Information obtained will be retained in a confidential file and separate from personnel records. This information may only be used in accordance with the provision of applicable federal laws, executive orders, and regulations. If you want more information about any of the sections, please check with a company representative.</p>
      </div>);
    }
    if (category === ControlGroupCategory.ofccp) {
      before = (<div class="form-control">
        <h2>Voluntary Self-Identification of Disability</h2>
        <p>Form CC-305</p><p>OMB Control Number 1250-0005</p><p>Expires 04/30/2026</p>
        <h3>Why are you being asked to complete this form?</h3>
        <p>We are a federal contractor or subcontractor. The law requires us to provide equal employment opportunity to qualified people with disabilities. We have a goal of having at least 7% of our workers as people with disabilities. The law says we must measure our progress towards this goal. To do this, we must ask applicants and employees if they have a disability or have ever had one. People can become disabled, so we need to ask this question at least every five years.</p>
        <p>Completing this form is voluntary, and we hope that you will choose to do so. Your answer is confidential. No one who makes hiring decisions will see it. Your decision to complete the form and your answer will not harm you in any way. If you want to learn more about the law or this form, visit the U.S. Department of Labor’s Office of Federal Contract Compliance Programs (OFCCP) website at <a class="link" href="https://www.dol.gov/ofccp">www.dol.gov/ofccp</a>.</p>
        <p><strong>How do you know if you have a disability?</strong></p>
        <p>A disability is a condition that substantially limits one or more of your “major life activities.” If you have or have ever had such a condition, you are a person with a disability. <strong>Disabilities include, but are not limited to:</strong></p>
        <ul class="indented">
          <li>Alcohol or other substance use disorder (not currently using drugs illegally)</li>
          <li>Autoimmune disorder, for example, lupus, fibromyalgia, rheumatoid arthritis, HIV/AIDS</li>
          <li>Blind or low vision</li>
          <li>Cancer (past or present)</li>
          <li>Cardiovascular or heart disease</li>
          <li>Celiac disease</li>
          <li>Cerebral palsy</li>
          <li>Deaf or serious difficulty hearing</li>
          <li>Diabetes</li>
          <li>Disfigurement, for example, disfigurement caused by burns, wounds, accidents, or congenital disorders</li>
          <li>Epilepsy or other seizure disorder</li>
          <li>Gastrointestinal disorders, for example, Crohn's Disease, irritable bowel syndrome</li>
          <li>Intellectual or developmental disability</li>
          <li>Mental health conditions, for example, depression, bipolar disorder, anxiety disorder, schizophrenia, PTSD</li>
          <li>Missing limbs or partially missing limbs</li>
          <li>Mobility impairment, benefiting from the use of a wheelchair, scooter, walker, leg brace(s) and/or other supports</li>
          <li>Nervous system condition, for example, migraine headaches, Parkinson’s disease, multiple sclerosis (MS)</li>
          <li>Neurodivergence, for example, attention-deficit/hyperactivity disorder (ADHD), autism spectrum disorder, dyslexia, dyspraxia, other learning disabilities</li>
          <li>Partial or complete paralysis (any cause)</li>
          <li>Pulmonary or respiratory conditions, for example, tuberculosis, asthma, emphysema</li>
          <li>Short stature (dwarfism)</li>
          <li>Traumatic brain injury</li>
        </ul>
      </div>
      );
    } else if (category === ControlGroupCategory.vevraa) {
      before = (<div class="form-control">
        <h3>Why Are You Being Asked to Complete This Form?</h3>
        <p>We are a Government contractor subject to the Vietnam Era Veterans' Readjustment Assistance Act of 1974, as amended by the Jobs for Veterans Act of 2002, 38 U.S.C. 4212 (VEVRAA). VEVRAA requires Government contractors to take affirmative action to employ and advance in employment protected veterans. To help us measure the effectiveness of our outreach and recruitment efforts of veterans, we are asking you to tell us if you are a veteran covered by VEVRAA. Completing this form is completely voluntary, but we hope you fill it out. Any answer you give will be kept private and will not be used against you in any way.</p>
        <p>For more information about this form or the equal employment obligations of Federal contractors, visit the U.S. Department of Labor’s Office of Federal Contract Compliance Programs (OFCCP) website at <a class="link" href="https://www.dol.gov/ofccp">www.dol.gov/ofccp</a>.</p>
        <h3>How Do You Know if You Are a Veteran Protected by VEVRAA?</h3>
        <p>Contrary to the name, VEVRAA does not just cover Vietnam Era veterans. It covers several categories of veterans from World War II, the Korean conflict, the Vietnam era, and the Persian Gulf War which is defined as occurring from August 2, 1990 to the present.</p>
        <p>If you believe you belong to any of the categories of protected veterans please indicate by checking the appropriate box below. The categories are defined on the next page and explained further in an <a class="link" href="https://www.dol.gov/agencies/ofccp/veterans/protected-veterans">“Am I a Protected Veteran?”</a> infographic provided by OFCCP.</p>
        <h3>What Categories of Veterans Are “Protected” by VEVRAA?</h3>
        <p>“Protected” veterans include the following categories: (1) disabled veterans; (2) recently separated veterans; (3) active duty wartime or campaign badge veterans; and (4) Armed Forces service medal veterans. These categories are defined below.</p>
        <p>1. A “disabled veteran” is one of the following:</p>
        <ul class="indented">
          <li>a veteran of the U.S. military, ground, naval or air service who is entitled to compensation (or who but for the receipt of military retired pay would be entitled to compensation) under laws administered by the Secretary of Veterans Affairs; or</li>
          <li>a person who was discharged or released from active duty because of a service-connected disability.</li>
        </ul>
        <p>2. A “recently separated veteran” means any veteran during the three-year period beginning on the date of such veteran's discharge or release from active duty in the U.S. military, ground, naval, or air service.</p>
        <p>3. An “active duty wartime or campaign badge veteran” means a veteran who served on active duty in the U.S. military, ground, naval or air service during a war, or in a campaign or expedition for which a campaign badge has been authorized under the laws administered by the Department of Defense.</p>
        <p>4. An “Armed forces service medal veteran” means a veteran who, while serving on active duty in the U.S. military, ground, naval or air service, participated in a United States military operation for which an Armed Forces service medal was awarded pursuant to Executive Order 12985.</p>
      </div>);
    }
    return before;
  };

  const errorsForInput = (question_id: string): IValidationError[] => {
    return errors.filter((err) => {
      return err.id == question_id;
    });
  }

  return (
    <>
      {beforeBlock()}

      {questions.map(question =>
        <ApplyControl errors={errorsForInput(question.id)} groupCategory={category} onBeforeSubmit={onBeforeSubmit} {...question} />
      )}

      {afterBlock()}
    </>
  );
}

const ApplyControl: FunctionComponent<IApplyControlProps> = ({ errors, groupCategory, id, label, required, onBeforeSubmit, options, type, validation }) => {
  const datePickerRef = useRef(null);
  const [fileName, setFileName] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const noResponseLabel = groupCategory in NoResponseOptionQuestions ? 'I do not want to answer' : 'No response';

  useEffect(() => {
    if (type !== "date") {
      return;
    }
    datePickerRef.current = TinyDatePicker(`#${generateIdAttribute(id)}`, {
      format: (date) => {
        return date.toISOString().substr(0, 10);
      },
      mode: 'dp-below',
      parse: (str) => {
        const pattern = /^\d{4}-\d{2}-\d{2}$/;
        const match = str.match(pattern);
        if (match == undefined) {
          return new Date();
        }

        // The local timezone midnight suffix prevents a date shift bug
        return Date.parse(`${str}T00:00:00`);
      },
    });
  }, []);

  useEffect(() => {
    if (datePickerRef.current === null) {
      return;
    }

    datePickerRef.current.close();
  }, [onBeforeSubmit])

  let props: any = {
    id: generateIdAttribute(id),
    name: generateIdAttribute(id),
    required: required
  };

  let detailLine;

  if (new Set(['text', 'email', 'textarea']).has(type)) {
    props.maxlength = validation.max;
    props.minlength = validation.min;
  }


  if (new Set(['text', 'email', 'file']).has(type)) {
    props.type = type;
  }

  if (type === "file") {
    props.accept = ".pdf,.docx,.odt,.rtf,.txt"
    detailLine = "Allowed file-types: pdf, docx, odt, rtf, txt";
  }

  const classes = () => {
    const values = ['form-control'];
    if (errors.length > 0) {
      values.push("error")
    }
    return values.join(" ");
  }

  const onChangeFileInput = (event: any) => {
    const hasFile = "files" in event.target && event.target.files.length === 1;
    if (!hasFile) {
      return;
    }

    const file = event.target.files[0];
    if (file === null || file === undefined) {
      return;
    }

    if (file.size > 9500000) {
      // These could become unnecessary when directly manipulating input.files
      (document.querySelector(`#${id}`) as any).value = "";
      setFileName(null);
      return;
    }

    // This version works in tests, but not properly in browser:
    // (document.querySelector(`#${id}`) as any).fileList = file;

    // This version works in browser, but not in tests:
    // (document.querySelector(`#${id}`) as any).files = event.target.files;
    // https://github.com/testing-library/user-event/issues/1223

    // To-do: revisit once that issue is addressed.

    validateFileName(file);
    setFileName(file.name);
  }

  const onDrop = (event: any) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer ? event.dataTransfer.files : event.target.files;
    if (droppedFiles.length !== 1) {
      return;
    }

    const file = droppedFiles[0];
    if (file.size > 9500000) {
      return;
    }

    // Previously I was manipulating fileList but that actually does not work
    // as expected in the browser. It fails to update the input itself.
    // (document.querySelector(`#${id}`) as any).fileList = file;
    (document.querySelector(`#${id}`) as any).files = droppedFiles;
    validateFileName(file);
    setFileName(file.name);
  }

  const onDragOver = (event: any) => {
    event.preventDefault();
  }

  const removeFile = (event: any) => {
    (document.querySelector(`#${id}`) as any).value = "";
    setFileName(null);
    setValidationError(null);
  }

  const renderErrorMessages = () => (<>
    {errors.map((err) =>
      <div class="error-message">{err.message}</div>
    )}
  </>);

  const validateFileName = (file: any) => {
    const fileNameTooLong = file.name.length > validation.max;
    const fileNameTooShort = file.name.length < validation.min;
    let validationError = null;

    if (fileNameTooLong) {
      validationError = `File name too long. ${validation.max} characters maximum.`;
    } else if (fileNameTooShort) {
      validationError = `File name too short. ${validation.min} characters minimum.`;
    }

    setValidationError(validationError)
  }

  const validateTextInput = (event: any) => {
    const hideValidationError = event.target.value === "";
    const isValid = event.target.validity.valid;
    setValidationError(isValid || hideValidationError ? null : event.target.validationMessage);
  }

  return (
    <div class={classes()} data-testid={id}>
      {/*
        Label:
        Inputs of type radio and checkbox have labels without a "for"
        attribute. All other input types have a "for" attribute.
      */}
      {
        (type === OptionEnabledQuestionType.radio || type === OptionEnabledQuestionType.checkbox) &&
        <div class="label">
          <label>
            <span>{label}</span>
            {generateRequiredIndicator(required)}
          </label>
        </div>
      }
      {
        (type !== OptionEnabledQuestionType.radio && type !== OptionEnabledQuestionType.checkbox) &&
        <div class="label">
          <label for={generateIdAttribute(id)}>
            <span>{label}</span>
            {generateRequiredIndicator(required)}
          </label>
        </div>
      }

      {/*
        The input itself
      */}
      {(type === QuestionType.text || type === QuestionType.email) &&
        <input class="input input-primary" onKeyUp={validateTextInput} {...props}></input>
      }
      {type === QuestionType.textarea &&
        <textarea class="textarea textarea-primary" onKeyUp={validateTextInput} {...props}></textarea>
      }
      {type === QuestionType.date &&
        <input class="input input-primary" type="text" placeholder="YYYY-MM-DD" {...props}></input>
      }
      {type === QuestionType.file &&
        <div class="file-container">
          <input class="input-file" {...props} onChange={onChangeFileInput}></input>
          <label class="dropzone-label-container" for={generateIdAttribute(id)} onDragOver={onDragOver} onDrop={onDrop}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" height="40px">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15m0-3-3-3m0 0-3 3m3-3V15" />
            </svg>
            <p><span class="text-emphasis">Click to upload</span> or drop a file here.</p>
            <p class="italic">
              {detailLine}
            </p>
            <p class="italic">
              9.5MB file size limit.
            </p>
          </label>
          {fileName == null &&
            <span class="helper-text">No file selected</span>
          }
          {fileName !== null &&
            <div class="attached-file">
              <button onClick={(e) => { removeFile(e); }} type="button">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4">
                  <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                </svg>
              </button>
              <span class="helper-text">{fileName}</span>
            </div>
          }
        </div>
      }
      {
        type === OptionEnabledQuestionType.radio &&
        <>
          {options.map((option, index) =>
            <div class="option-row">
              <input
                class="radio radio-primary"
                id={generateIdAttribute(id, `-${index}`)}
                name={generateIdAttribute(id)}
                {...{ required }}
                type="radio"
                value={index}>
              </input>
              <label for={generateIdAttribute(id, `-${index}`)}>{option}</label>
            </div>
          )}
        </>
      }
      {
        type === OptionEnabledQuestionType.checkbox &&
        <>
          {options.map((option, index) =>
            <div class="option-row">
              <input
                class="checkbox checkbox-primary"
                id={generateIdAttribute(id, `-${index}`)}
                name={generateIdAttribute(id)}
                type="checkbox"
                value={1}>
              </input>
              <label for={generateIdAttribute(id, `-${index}`)}>{option}</label>
            </div>
          )}
        </>
      }
      {
        type === OptionEnabledQuestionType.select &&
        <select
          class="select select-primary"
          id={generateIdAttribute(id)}
          name={generateIdAttribute(id)}
          {...{ required }}>
          <option value={""}>{noResponseLabel}</option>
          {options.map((option, index) =>
            <option value={index}>{option}</option>
          )}
        </select>
      }
      {/**
       validation errors
       */}
      {
        validationError !== null &&
        <div class="error-message">
          {validationError}
        </div>
      }
      {renderErrorMessages()}
    </div>
  );
}

const Apply: FunctionComponent<IApplyProps> = ({ context }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorMessagesForInputs, setErrorMessagesForInputs] = useState<IValidationError[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [onBeforeSubmit, setOnBeforeSubmit] = useState(0);
  const [questionIdToIndexMap, setQuestionIdToIndexMap] = useState(null);

  useEffect(() => {
    const questionIdMap = {};
    const questionIds = [];
    for (const groupIndex in context.question_groups) {
      for (const question of context.question_groups[groupIndex]) {
        questionIds.push(question);
      }
    }
    const questionIdSet = new Set(questionIds);

    for (const index in context.questions) {
      const questionId = context.questions[index].id;
      if (questionIdSet.has(questionId)) {
        questionIdMap[questionId] = index;
      }
    }

    setQuestionIdToIndexMap(questionIdMap);
    setIsInitializing(false);
  }, []);

  const errorMessageForGroup = (groupIndex: number): IValidationError[] => {
    return errorMessagesForInputs.filter((err) => {
      return context.question_groups[groupIndex].indexOf(err.id) > -1;
    });
  }

  const getQuestions = (questions: string[]): IQuestion[] => {
    const output = [];

    for (const questionId of questions) {
      output.push(context.questions[questionIdToIndexMap[questionId]]);
    }

    return output;
  }

  const getQuestionsCategory = (questions: string[]): ControlGroupCategory => {
    switch (questions[0]) {
      case "eeo_gender":
        return ControlGroupCategory.eeo;
      case "ofccp":
        return ControlGroupCategory.ofccp;
      case "vevraa":
        return ControlGroupCategory.vevraa;
      default:
        return ControlGroupCategory.other;
    }
  }

  const handleSubmission = (e) => {
    e.preventDefault();
    const payload = {};
    payload['role_id'] = context.role_id;
    payload['responses'] = [];

    const querystringParameters = new URLSearchParams(window.location.search);
    const source = querystringParameters.get("source");
    if (source !== null) {
      payload['source'] = source;
    }

    const filesToUpload: IFileToUpload[] = [];
    for (const group of context.question_groups) {
      for (const question of getQuestions(group)) {
        const response = {
          id: question.id,
          value: []
        };

        switch (question.type) {
          case OptionEnabledQuestionType.checkbox:
            for (const optionIndex in question.options) {
              if (e.target.elements[`${question.id}-${optionIndex}`].checked) {
                response.value.push(question.options[optionIndex]);
              }
            }
            break;
          case OptionEnabledQuestionType.radio:
          case OptionEnabledQuestionType.select:
            const selectionIndex = e.target.elements[`${question.id}`].value;
            let selectValue: string = "";
            if (selectionIndex !== "") { // empty string means no selection
              selectValue = question.options[parseInt(selectionIndex)];
            }
            response.value.push(selectValue);
            break;
          case QuestionType.file:
            const files = e.target.elements[`${question.id}`].files;
            let fileValue: string = "";

            if (files !== null && files.length > 0) {
              fileValue = files[0].name;
              filesToUpload.push({ id: question.id, file: files[0] });
            }
            // Still push empty values into response to guarantee
            // the validate payload contains them
            response.value.push(fileValue);
            break;
          default:
            response.value.push(e.target.elements[`${question.id}`].value);
        }

        payload['responses'].push(response);
      }
    }

    setOnBeforeSubmit(onBeforeSubmit + 1);
    setErrorMessage(null);
    setErrorMessagesForInputs([]);
    setIsProcessing(true);

    const apiHost = `https://${window.location.hostname.replace('apply', 'public-apply-api')}`;

    const defaultError = {
      message: 'An unexpected error occurred. Please try again later.',
    };

    fetch(`${apiHost}/validate`, {
      method: "post",
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json; charset=UTF-8'
      }
      // Fetch returns a stream which can only be read once, therefore it is
      // immediately passed through a .then callback in the following line.
    }).then(r => r.json().then((json): IValidationResponseBody => ({ status: r.status, body: json }))
    ).then((validationResponse): Promise<any> => {
      if (validationResponse.status !== 200) {
        const errors = (validationResponse.body as IValidationErrorBody).errors ?? [];
        if (errors.length > 0) {
          return Promise.reject({ message: 'Your submission contains invalid input. Please address the errors and try again.', errors });
        }
        return Promise.reject({ message: 'Validation failure', errors: [] });
      }

      const fileUploadPromises = [];
      for (const fileToUpload of filesToUpload) {
        const fileForm = new FormData();
        fileForm.append("submit_jwt", (validationResponse.body as IValidationSuccessBody).submit_jwt);
        fileForm.append("id", fileToUpload.id);
        fileForm.append("file", fileToUpload.file);

        const fileUploadPromise = fetch(`${apiHost}/file-upload`, {
          method: "post",
          body: fileForm,
        }).then((fileUploadResponse): Promise<any> => {
          if (fileUploadResponse.status !== 204) {
            return Promise.reject({ message: `The file '${fileToUpload.file.name}' failed to upload.`, errors: [] });
          }
          return Promise.resolve();
        });

        fileUploadPromises.push(fileUploadPromise);
      }

      // Make the .all resolve even if there are no file uploads
      if (fileUploadPromises.length === 0) {
        fileUploadPromises.push(Promise.resolve());
      }

      return Promise.all(fileUploadPromises)
        .then(() => {
          return fetch(`${apiHost}/submit`, {
            method: "post",
            body: JSON.stringify({ "submit_jwt": (validationResponse.body as IValidationSuccessBody).submit_jwt }),
            headers: {
              'Content-Type': 'application/json; charset=UTF-8'
            }
          }).then((submitResponse): Promise<any> => {
            if (submitResponse.status !== 204) {
              // to-do: make this endpoint also return validation errors
              // handle those as well as generic errors
              return Promise.reject({ message: 'An error occurred and your application was not submitted. Please try again later.', errors: [] });
            }
            return Promise.resolve();
          });
        });
    }).then(() => {
      setIsSubmitted(true);
      setIsProcessing(false);
    }).catch((error) => {
      let errorMessage = defaultError.message;

      if (typeof error == "object" && "message" in error) {
        errorMessage = error.message;
      }

      if (typeof error == "object" && "errors" in error && error.errors.length > 0) {
        setErrorMessagesForInputs(error.errors);

        for (const server_error of error.errors) {
          if (server_error.id == "") {
            // Explicitly append any empty id errors to the form-level error message
            errorMessage += ` ${server_error.message}`;
          }
        }
      }

      setErrorMessage(errorMessage);
      setIsProcessing(false);
    });
  }

  return (<>
    {isSubmitted &&
      <div class="success-container">
        <h1>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
          <span>Success</span>
        </h1>
        <p>Your application has been received. Thank you for applying!</p>
      </div>
    }
    {!isSubmitted && !isInitializing &&
      <form class="questions-form" onSubmit={handleSubmission}>
        <div class="questions-container">
          {context.question_groups.map((group, index) =>
            <ApplyControlGroup category={getQuestionsCategory(group)} errors={errorMessageForGroup(index)} onBeforeSubmit={onBeforeSubmit} questions={getQuestions(group)} />
          )}
        </div>
        {errorMessage != null &&
          <div class="error-container">
            <h1>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
              <span>Error</span>
            </h1>
            <p>{errorMessage}</p>
          </div>
        }
        <div class="button-container">
          <button class={isProcessing ? 'button-submit is-processing' : 'button-submit'} disabled={context.preview || isProcessing} type="submit">
            {!isProcessing &&
              <span>
                Submit Application
              </span>
            }
            {isProcessing &&
              <span>
                <span>&nbsp;</span>
                <span class="loader"></span>
              </span>
            }
          </button>
        </div>
      </form>
    }
  </>);
};

function Mount() {
  const contextElement = document.getElementById("apply-app-context");
  const mount = document.getElementById("apply-app-mount");
  render(h(Apply, { context: JSON.parse(contextElement!.textContent!) }), mount!);

  const pane = document.querySelector('.right-pane');
  const to_page_1 = document.querySelector('.navigate-to-page-1');
  const to_page_2 = document.querySelector('.navigate-to-page-2');
  const to_page_button = document.querySelector('.button-to-page-2');

  function changeToPage(page) {
    const classes = new Set(pane!.classList);
    if (!classes.has(page)) {
      pane!.classList.toggle('page1');
      pane!.classList.toggle('page2');
      to_page_1!.classList.toggle('tab-inactive');
      to_page_1!.classList.toggle('tab-active');
      to_page_2!.classList.toggle('tab-inactive');
      to_page_2!.classList.toggle('tab-active');
    }
  }

  to_page_1!.addEventListener('click', () => changeToPage("page1"));
  to_page_2!.addEventListener('click', () => changeToPage("page2"));
  to_page_button!.addEventListener('click', () => {
    changeToPage("page2");
    document.querySelector("html")!.scroll({ top: 0, left: 0, behavior: "smooth" });
  });
}

export {
  Apply,
  Mount
}